import { useMemo } from "react";

import { StateVariant } from "@/components/types";
import {
  FilteredPricingEngineRequest,
  FilteredResolvedBlockchainWithdrawal,
  InstructionState,
  PaymentWithdrawalState,
  ResolvedPayment,
  TradeFiltered,
} from "@generated/types";

interface UsePricingStatusOptions {
  sourceData: FilteredPricingEngineRequest | null | undefined;
  isPaymentFlow: boolean;
}

interface UsePricingStatusResult {
  isTradeScheduled: boolean;
  isPaymentScheduled: boolean;
  isTransactionScheduled: boolean;
  isPaymentCancelled: boolean;
  animatedStateCircle: StateVariant;
  identifier: string | undefined;
  exchangeRate: number;
}

/**
 * Derives display-level status from the current pricing request data.
 *
 * Responsibilities:
 * - Detects scheduled/cancelled states for success page display
 * - Computes animatedStateCircle variant (success/warn/error)
 * - Extracts transaction identifier (reference)
 * - Computes exchange rate from instruction amounts
 */
export function usePricingStatus({
  sourceData,
  isPaymentFlow,
}: UsePricingStatusOptions): UsePricingStatusResult {
  return useMemo(() => {
    const tradeInstruction = sourceData?.instructions?.[0];
    const transaction = tradeInstruction?.transaction as
      | { trades?: TradeFiltered[]; reference?: string; id?: string }
      | undefined;
    const deliveryInstruction = tradeInstruction?.deliveryInstruction;

    // Detect scheduled trade (unsettled first trade)
    const isTradeScheduled = transaction?.trades?.[0]?.settled === false;

    // Detect scheduled payment (FIAT or blockchain)
    const paymentState = (
      deliveryInstruction?.payment as { paymentState?: string } | undefined
    )?.paymentState;
    const blockchainWithdrawal =
      deliveryInstruction?.payment as unknown as FilteredResolvedBlockchainWithdrawal;
    const isPaymentScheduled =
      paymentState === PaymentWithdrawalState.Scheduled ||
      blockchainWithdrawal?.withdrawalState ===
        PaymentWithdrawalState.Scheduled;

    const isTransactionScheduled = isTradeScheduled || isPaymentScheduled;

    // Detect cancelled payment
    const payment = deliveryInstruction?.payment as
      | ResolvedPayment
      | FilteredResolvedBlockchainWithdrawal
      | undefined;
    const isPaymentCancelled =
      payment?.instructionState === InstructionState.Cancelled;

    // Priority: confirmed > scheduled > cancelled
    const animatedStateCircle: StateVariant = isPaymentCancelled
      ? "error"
      : isTransactionScheduled
        ? "warn"
        : "success";

    // Extract transaction identifier
    let identifier: string | undefined;
    if (isPaymentFlow) {
      const paymentReference = (
        deliveryInstruction?.payment as unknown as ResolvedPayment
      )?.reference;
      identifier = paymentReference || transaction?.reference;
    } else {
      identifier = transaction?.reference;
    }

    // Calculate exchange rate
    let exchangeRate = 0;
    if (tradeInstruction?.payAmount && tradeInstruction?.receiveAmount) {
      exchangeRate =
        tradeInstruction.receiveAmount / tradeInstruction.payAmount;
    }

    return {
      isTradeScheduled,
      isPaymentScheduled,
      isTransactionScheduled,
      isPaymentCancelled,
      animatedStateCircle,
      identifier,
      exchangeRate,
    };
  }, [sourceData, isPaymentFlow]);
}
